import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { dedupApi, type DedupCandidate } from '@/lib/api/client'
import { CandidateCard } from './CandidateCard'
import { DedupActivityLog } from './DedupActivityLog'
import {
  GitMerge,
  Zap,
  Loader2,
  CheckCircle,
  ChevronUp,
  ChevronDown,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

type DomainFilter = 'all' | 'market' | 'business' | 'solution'

const DOMAIN_CHIPS: { value: DomainFilter; label: string; color: string }[] = [
  { value: 'all', label: 'All', color: 'bg-warm-200 text-warm-700' },
  { value: 'market', label: 'Market', color: 'bg-market/10 text-market' },
  { value: 'business', label: 'Business', color: 'bg-business/10 text-business' },
  { value: 'solution', label: 'Solution', color: 'bg-solution/10 text-solution' },
]

export function DeduplicationView() {
  const { currentAccount } = useAuthStore()
  const { currentProject } = useProjectStore()
  const queryClient = useQueryClient()

  const [domainFilter, setDomainFilter] = useState<DomainFilter>('all')
  const [selectedCandidateIdx, setSelectedCandidateIdx] = useState<number>(-1)
  const [selectedUris, setSelectedUris] = useState<Set<string>>(new Set())

  const accountId = currentAccount?.id || ''
  const projectId = currentProject?.id || ''

  // Fetch candidates
  const { data: candidatesData, isLoading: loadingCandidates } = useQuery({
    queryKey: ['dedup-candidates', accountId, projectId, domainFilter],
    queryFn: () => dedupApi.getCandidates(accountId, projectId, domainFilter === 'all' ? undefined : domainFilter),
    enabled: !!accountId && !!projectId,
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['dedup-stats', accountId, projectId],
    queryFn: () => dedupApi.getStats(accountId, projectId),
    enabled: !!accountId && !!projectId,
  })

  // Fetch events
  const { data: eventsData } = useQuery({
    queryKey: ['dedup-events', accountId, projectId],
    queryFn: () => dedupApi.getEvents(accountId, projectId),
    enabled: !!accountId && !!projectId,
  })

  // Run dedup mutation
  const runDedup = useMutation({
    mutationFn: () => dedupApi.runDedup(accountId, projectId, domainFilter === 'all' ? undefined : domainFilter),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dedup-candidates'] })
      queryClient.invalidateQueries({ queryKey: ['dedup-stats'] })
      queryClient.invalidateQueries({ queryKey: ['dedup-events'] })
    },
  })

  // Merge mutation
  const mergeMutation = useMutation({
    mutationFn: ({ canonicalUri, deprecatedUri }: { canonicalUri: string; deprecatedUri: string }) =>
      dedupApi.merge(accountId, projectId, canonicalUri, deprecatedUri),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dedup-candidates'] })
      queryClient.invalidateQueries({ queryKey: ['dedup-stats'] })
      queryClient.invalidateQueries({ queryKey: ['dedup-events'] })
    },
  })

  // Dismiss mutation
  const dismissMutation = useMutation({
    mutationFn: ({ entityAUri, entityBUri }: { entityAUri: string; entityBUri: string }) =>
      dedupApi.dismiss(accountId, projectId, entityAUri, entityBUri),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dedup-candidates'] })
      queryClient.invalidateQueries({ queryKey: ['dedup-stats'] })
    },
  })

  const candidates = candidatesData?.candidates || []
  const events = eventsData?.events || []
  const selectedCandidate = selectedCandidateIdx >= 0 ? candidates[selectedCandidateIdx] : null

  const handleMerge = useCallback((candidate: DedupCandidate) => {
    // Select canonical based on which entity is stronger
    const canonicalUri = candidate.entityAConfidence >= candidate.entityBConfidence
      ? candidate.entityAUri : candidate.entityBUri
    const deprecatedUri = canonicalUri === candidate.entityAUri
      ? candidate.entityBUri : candidate.entityAUri
    mergeMutation.mutate({ canonicalUri, deprecatedUri })
  }, [mergeMutation])

  const handleDismiss = useCallback((candidate: DedupCandidate) => {
    dismissMutation.mutate({ entityAUri: candidate.entityAUri, entityBUri: candidate.entityBUri })
  }, [dismissMutation])

  const handleBulkMerge = useCallback(() => {
    for (const candidate of candidates) {
      const key = `${candidate.entityAUri}|${candidate.entityBUri}`
      if (selectedUris.has(key)) {
        handleMerge(candidate)
      }
    }
    setSelectedUris(new Set())
  }, [candidates, selectedUris, handleMerge])

  const toggleSelect = useCallback((candidate: DedupCandidate) => {
    const key = `${candidate.entityAUri}|${candidate.entityBUri}`
    setSelectedUris((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    if (selectedUris.size === candidates.length) {
      setSelectedUris(new Set())
    } else {
      setSelectedUris(new Set(candidates.map((c) => `${c.entityAUri}|${c.entityBUri}`)))
    }
  }, [candidates, selectedUris])

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-full text-text-muted">
        Select a project to view deduplication candidates.
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-warm-50">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle bg-surface">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-market/10 flex items-center justify-center">
            <GitMerge className="w-4 h-4 text-market" />
          </div>
          <div>
            <h1 className="text-lg font-display font-semibold text-text">Entity Deduplication</h1>
            <p className="text-xs text-text-muted">Detect and merge duplicate entities across sources</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Domain filter chips */}
          <div className="flex gap-1.5">
            {DOMAIN_CHIPS.map((chip) => (
              <button
                key={chip.value}
                onClick={() => setDomainFilter(chip.value)}
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-medium transition-all',
                  domainFilter === chip.value
                    ? chip.color + ' ring-1 ring-current/20'
                    : 'bg-warm-100 text-text-muted hover:bg-warm-200'
                )}
              >
                {chip.label}
              </button>
            ))}
          </div>

          <Button
            onClick={() => runDedup.mutate()}
            disabled={runDedup.isPending}
            size="sm"
            className="bg-solution hover:bg-solution-dark text-white gap-1.5"
          >
            {runDedup.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Zap className="w-3.5 h-3.5" />
            )}
            Run Dedup
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center gap-6 px-6 py-2.5 border-b border-border-subtle bg-warm-50 text-xs">
        <span className="text-text-muted">
          Total: <span className="font-medium text-text">{stats?.totalEntities ?? '—'}</span>
        </span>
        <span className="text-text-muted">
          Duplicates: <span className="font-medium text-text">{stats?.duplicatesFound ?? '—'}</span>
        </span>
        <span className="text-text-muted">
          Auto-merged: <span className="font-medium text-market">{stats?.autoMerged ?? '—'}</span>
        </span>
        <span className="text-text-muted">
          Pending: <span className="font-medium text-solution">{stats?.pendingReview ?? '—'}</span>
        </span>

        {selectedUris.size > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-text-secondary font-medium">{selectedUris.size} selected</span>
            <Button
              variant="outline"
              size="sm"
              className="h-6 text-xs gap-1"
              onClick={handleBulkMerge}
            >
              <GitMerge className="w-3 h-3" />
              Merge All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => setSelectedUris(new Set())}
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        )}
      </div>

      {/* Main content: candidate list + bottom preview */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Candidate list */}
        <div className="flex-1 overflow-y-auto">
          {loadingCandidates ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-text-muted" />
              <span className="ml-2 text-sm text-text-muted">Loading candidates...</span>
            </div>
          ) : candidates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-text-muted">
              <CheckCircle className="w-10 h-10 mb-3 text-market/40" />
              <p className="text-sm font-medium">No duplicate candidates found</p>
              <p className="text-xs mt-1">Run dedup analysis to scan for duplicates</p>
            </div>
          ) : (
            <div>
              {/* Select all header */}
              <div className="flex items-center gap-3 px-4 py-2 border-b border-border-subtle bg-warm-50 text-xs text-text-muted sticky top-0 z-10">
                <input
                  type="checkbox"
                  checked={selectedUris.size === candidates.length && candidates.length > 0}
                  onChange={selectAll}
                  className="rounded border-warm-300"
                />
                <span>{candidates.length} candidate{candidates.length !== 1 ? 's' : ''}</span>
              </div>

              {candidates.map((candidate, idx) => (
                <CandidateListItem
                  key={`${candidate.entityAUri}|${candidate.entityBUri}`}
                  candidate={candidate}
                  isSelected={selectedCandidateIdx === idx}
                  isChecked={selectedUris.has(`${candidate.entityAUri}|${candidate.entityBUri}`)}
                  onSelect={() => setSelectedCandidateIdx(idx)}
                  onToggleCheck={() => toggleSelect(candidate)}
                  onQuickMerge={() => handleMerge(candidate)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Bottom preview panel */}
        {selectedCandidate && (
          <div className="h-[320px] border-t border-border bg-surface flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b border-border-subtle bg-warm-50">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-text">Preview</span>
                <Badge variant="outline" className="text-xs">{selectedCandidate.entityType}</Badge>
                <DomainBadge domain={selectedCandidate.domain} />
                <SimilarityBadge score={selectedCandidate.similarityScore} />
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setSelectedCandidateIdx(Math.max(0, selectedCandidateIdx - 1))}
                  disabled={selectedCandidateIdx <= 0}
                >
                  <ChevronUp className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setSelectedCandidateIdx(Math.min(candidates.length - 1, selectedCandidateIdx + 1))}
                  disabled={selectedCandidateIdx >= candidates.length - 1}
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setSelectedCandidateIdx(-1)}
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            <CandidateCard
              candidate={selectedCandidate}
              onMerge={() => handleMerge(selectedCandidate)}
              onDismiss={() => handleDismiss(selectedCandidate)}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function CandidateListItem({
  candidate,
  isSelected,
  isChecked,
  onSelect,
  onToggleCheck,
  onQuickMerge,
}: {
  candidate: DedupCandidate
  isSelected: boolean
  isChecked: boolean
  onSelect: () => void
  onToggleCheck: () => void
  onQuickMerge: () => void
}) {
  const scorePercent = Math.round(candidate.similarityScore * 100)

  return (
    <div
      onClick={onSelect}
      className={cn(
        'flex items-center gap-3 px-4 py-2.5 border-b border-border-subtle cursor-pointer group transition-colors',
        isSelected ? 'bg-market/5 border-l-2 border-l-market' : 'hover:bg-warm-50 border-l-2 border-l-transparent'
      )}
    >
      <input
        type="checkbox"
        checked={isChecked}
        onChange={(e) => { e.stopPropagation(); onToggleCheck() }}
        onClick={(e) => e.stopPropagation()}
        className="rounded border-warm-300 flex-shrink-0"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-text truncate">{candidate.entityALabel}</span>
          <GitMerge className="w-3 h-3 text-text-muted flex-shrink-0" />
          <span className="text-sm text-text-secondary truncate">{candidate.entityBLabel}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-text-muted">{candidate.entityType}</span>
          <DomainBadge domain={candidate.domain} small />
        </div>
      </div>

      <SimilarityBadge score={candidate.similarityScore} />

      <Button
        variant="ghost"
        size="sm"
        className="h-7 text-xs opacity-0 group-hover:opacity-100 transition-opacity gap-1 text-market hover:text-market"
        onClick={(e) => { e.stopPropagation(); onQuickMerge() }}
      >
        <GitMerge className="w-3 h-3" />
        Merge
      </Button>
    </div>
  )
}

function DomainBadge({ domain, small }: { domain: string; small?: boolean }) {
  const colors: Record<string, string> = {
    market: 'bg-market/10 text-market border-market/20',
    business: 'bg-business/10 text-business border-business/20',
    solution: 'bg-solution/10 text-solution border-solution/20',
  }

  return (
    <span className={cn(
      'inline-flex items-center rounded-full border font-medium capitalize',
      small ? 'px-1.5 py-0 text-[10px]' : 'px-2 py-0.5 text-xs',
      colors[domain] || 'bg-warm-100 text-text-muted border-warm-200'
    )}>
      {domain}
    </span>
  )
}

function SimilarityBadge({ score }: { score: number }) {
  const percent = Math.round(score * 100)
  const color = percent >= 80 ? 'text-market' : percent >= 50 ? 'text-solution' : 'text-text-muted'

  return (
    <span className={cn('text-xs font-mono font-medium tabular-nums', color)}>
      {percent}%
    </span>
  )
}
